import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import type { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { NumericType } from 'src/AngularJS/Models/metric';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import type { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleManagerModuleSettings } from 'src/shared/models/module-manager/module-manager-settings.models';
import { ModuleHelperService } from '../../../reusables/services/module-helper.service';
import { GaugeData, GaugeDataItem, GaugeObject, GaugeOptions, ModuleReportType, ReportGroupType, CompanyGroupTypes } from './gauge.models';
import { ErrorCode } from "src/shared/models/error-code.model";
import { GaugeService } from './gauge.service';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'gauge',
    templateUrl: './gauge.component.html',
    styleUrls: ['./gauge.component.scss']
})
export class GaugeComponent implements OnInit, AfterViewInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild('gaugeContainer', { read: ElementRef }) gaugeContainer: ElementRef<HTMLDivElement>;

    data: GaugeDataItem;
    gaugeObject: GaugeObject;
    isNoMetric = false;
    isDateRangeTooWideIntraday = false;
    companyGroupTypes: CompanyGroupTypes[];
    noData = false;
    ngbModal: NgbModal;

    isAborting = false;
    isFirstLoad = true;
    isLoading = false;
    loadingPromise: Promise<ApiResponse<GaugeData>> = null;
    abortLoad: () => void = null;

    constructor(
        private cvApi: CVApiService,
        private moduleHelper: ModuleHelperService,
        private service: GaugeService,
        private cvLocalizationService: CVLocalizationService,
        private contextMenuService?: AgContextMenuService,
    ) {
    }

    async ngOnInit() {
        this.moduleManager.showLoader = true;
        const localizationPromises = [
            this.cvLocalizationService.GetSection("Gauge_Module"),
            this.cvLocalizationService.GetSection("Common"),
        ];
        await Promise.all(localizationPromises);
        this.moduleManager.registerEvents({'onDashboardActivated': () => {
            if (this.data)
                this.createGauge();
        }});
    }

    ngAfterViewInit(): void {
        this.moduleManager.setOptions({
            canRefresh: () => !this.isLoading,
            enableWidthClass: false,
            hasRefresh: true,
            onExport: format => this.service.loadDataForExport(this.moduleKey, format),
            onRefresh: isAutoRefresh => this.onRefresh(isAutoRefresh),
            onSettingsApplied: settings => this.onSettingsApplied(settings),
            refreshType: ModuleManagerRefreshType.Auto,
            widgetMode: false,
        });
    }

    onRefresh(isAutoRefresh: boolean): void {
        this.moduleManager.showLoader = !isAutoRefresh;
        this.loadData(isAutoRefresh);
    }

    onSettingsApplied(settings: ModuleManagerModuleSettings): void {
        // settings were reapplied, destroy gauge and recreate
        if (this.gaugeObject != null) {
            this.gaugeObject.destroy();
            this.gaugeObject = null;
        }

        this.moduleManager.showLoader = true;
        this.loadData(false);
    }

    createGauge(): void {
        // The gauge cannot handle decimal breakpoints, so we are allowing up to thousandths place and then turning into an integer
        let max_decimal_places_index = 0;
        const gauge_data = this.data.metricData.objectiveData;
        if (gauge_data !== null) {
            gauge_data.poDetails.forEach((pdo, index) => {
                let current_index = pdo.Value.toString().split(".");
                if (current_index.length > 1) {
                    let previous_index = gauge_data.poDetails[max_decimal_places_index].Value.toString().split(".");
                    if (previous_index.length > 1) {
                        if (previous_index[1].length < current_index[1].length) {
                            max_decimal_places_index = index;
                        }
                    } else {
                        max_decimal_places_index = index;
                    }

                }
            });
        }
        const objective_value = this.data.metricData.objectiveData?.poDetails?.[max_decimal_places_index]?.Value.toString().split(".");
        let decimal_length = 0;
        if (typeof objective_value !== 'undefined' && objective_value.length > 1) {
            decimal_length = objective_value[1]?.length;
        } else {
            decimal_length = 3;
        }
        const VALUE_MODIFIER_POWER = ++decimal_length;
        const VALUE_MODIFIER = Math.pow(10, VALUE_MODIFIER_POWER);
        const metric = this.data.metric;
        const metricData = this.data.metricData;

        // set gauge options
        const gaugeOptions: GaugeOptions = {
            // animDurationHover: 100,
            breakpoints: [],
            colorSegments: [],
            direction: metric.DefaultDirection,
            hover: true,
            // hoverInvert: true, // Set to true if the hover effect should be set to default
            minValue: 0,
            maxValue: 0,
            title: metric.MetricCustomName && metric.MetricCustomName !== "" ? metric.MetricCustomName+"*" : metric.Abbreviation,
            unit: '',
            value: metricData.MetricValue * VALUE_MODIFIER,
            valueFormat: (val: number) => {
                val = val / VALUE_MODIFIER;
                if (val >= gaugeOptions.maxValue / VALUE_MODIFIER) {
                    return this.moduleHelper.formatMetricValue(
                        metricData.MetricValue,
                        metric.NumericType,
                        metric.TimeType,
                        metric.CurrencyType
                    );
                } else {
                    return this.moduleHelper.formatMetricValue(val, metric.NumericType, metric.TimeType, metric.CurrencyType);
                }
            }
        };

        if (this.data.metricData.objectiveData) {
            // transform objective details array into an array of the breakpoint values
            gaugeOptions.breakpoints = this.data.metricData.objectiveData.poDetails.map(pod => pod.Value * VALUE_MODIFIER - 1);
            gaugeOptions.breakpoints.sort((a, b) => a - b);

            // create length to the last breakpoint by adding the length of the first breakpoint
            const lastValue = gaugeOptions.breakpoints[gaugeOptions.breakpoints.length - 1];
            const average = lastValue / gaugeOptions.breakpoints.length;

            if (this.data.metric.NumericType === NumericType.Percent && lastValue < VALUE_MODIFIER) {
                // metric is numeric type of Percent(2), the maximum value is VALUE_MODIFIER beacuse now we have cases with more than 3 decimal set in objectives
                gaugeOptions.maxValue = VALUE_MODIFIER;
            } else {
                gaugeOptions.maxValue = lastValue + average;
            }

            // transform objective colors array into an array of hex color segments
            gaugeOptions.colorSegments = this.data.metricData.objectiveData.poColors.map(poc => poc.HexColor);
            if (gaugeOptions.direction === 0) // reverse colors on down metrics
                gaugeOptions.colorSegments.reverse();
        } else {
            // fake gauge
            gaugeOptions.hover = false;
            gaugeOptions.breakpoints = [];
            gaugeOptions.colorSegments = ['#a8afb9']; // gray5
            gaugeOptions.minValue = 0;
            gaugeOptions.maxValue = gaugeOptions.value + 1;
        }

        if (this.gaugeObject == null) {
            this.gaugeObject = new Gauge(this.gaugeContainer.nativeElement, gaugeOptions);
        } else {
            this.gaugeObject.setOpts(gaugeOptions);
        }

        const isRenamed = metric.MetricCustomName && metric.MetricCustomName !== "";

        if(isRenamed){
        // Adding a <title> element since tspan does not have a title attr
        const titleClass = document.getElementById("titleClass");
        const titleString = this.cvLocalizationService.getCachedText("Common", "Original_Metric", "Original Metric Name") + ": \n" + " " + metric.Abbreviation;
        const new_html = "<title> "+ titleString+" </title>" +titleClass.innerHTML;
        document.getElementById("titleClass").innerHTML = new_html;
        }
    }

    async loadData(isAutoRefresh): Promise<void> {
        if (this.loadingPromise) { // cancel current load
            this.isAborting = true;
            this.abortLoad?.();
            this.loadingPromise = null;
            this.abortLoad = null;
        }

        this.isLoading = true;
        const abort = new Promise<void>((resolve, reject) => this.abortLoad = () => resolve());
        this.loadingPromise = this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad, abort);

        try {
            const response = await this.loadingPromise;

            if (response.status_code === 200) {
                if (response.data.Error === ErrorCode.NoMetric) {
                    this.isNoMetric = true;
                } else if (response.data.Error === ErrorCode.DateRangeTooWideIntraday) {
                    this.isDateRangeTooWideIntraday = true;
                } else if (response.data.Error === ErrorCode.NullMetricData) {
                    this.noData = true;
                } else {
                    this.isNoMetric = false;
                    this.isDateRangeTooWideIntraday = false;
                    this.noData = false;
                    this.data = response.data.metrics[0];
                    this.companyGroupTypes = response.data.companyGroupTypes;
                    if (this.isFirstLoad) {
                        this.setMenuItems();
                    }
                    this.createGauge();
                    this.moduleManager.setAutoRefreshReady();
                }

                this.moduleManager.showLoader = false;
            } else {
                throw response;
            }
        } catch (e) {
            if (this.isAborting) {
                this.isAborting = false;
                return;
            }

            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.loadingPromise = null;
            this.isLoading = false;
            this.isFirstLoad = false;
            this.moduleManager.container.showDateRange = true;
        }
    }

    getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Gauge_Module", key, defaultValue);
    }

    getContextMenuParams(groupType: ReportGroupType, groupLevel: number) {
        const moduleInfo = this.moduleManager?.getModuleInfo();
        const moduleSettings = this.moduleManager?.getModuleSettings();
        return {
            moduleKey: this.moduleKey,
            moduleTypeId: moduleInfo.ModuleID,
            groupData: {
                groupID: null,
                groupName: moduleSettings?.selectedGroupName,
                groupLevel,
                groupHierarchy: moduleSettings?.parentGroupLevelName,
                groupType,
                teamUUID: null,
                agentUUID: null,
            },
            isParentAgGridModule: false,
        };
    }

    openRangeModal() {
        this.ngbModal.open("Gauge Range Modal!");
    }

    setMenuItems() {

        const openReport = (groupLevel: number, moduleType: ModuleReportType, groupType: ReportGroupType) => {
            const contextMenuParams = this.getContextMenuParams(groupType, groupLevel);
            if (moduleType === ModuleReportType.Interval) {
                this.contextMenuService.intervalReportClicked.next(contextMenuParams);
            } else if (moduleType === ModuleReportType.Summary) {
                this.contextMenuService.summaryReportClicked.next(contextMenuParams);
            }
        };

        //reverse companyGroupTypes array to show the groups in the order they are in the slide-out settings
        this.companyGroupTypes = this.companyGroupTypes.reverse();

        const intervalEmpGroups = [];
        const summaryEmpGroups = [];
        const intervalContactGroups = [];
        const summaryContactGroups = [];

        const employeeGroupsHeader: { name: string; type: ModuleManagerMenuItemType }[] = [{
            name: this.getLocaleText("Employee_Groups", "Employee Groups"),
            type: ModuleManagerMenuItemType.HeaderItem,
        }];

        const contactGroupsHeader: { name: string; type: ModuleManagerMenuItemType }[] = [{
            name: this.getLocaleText("Contact_Groups", "Contact Groups"),
            type: ModuleManagerMenuItemType.HeaderItem,
        }];

        this.companyGroupTypes.forEach((groupType) => {
            if (groupType.BaseGroupType === ReportGroupType.Employee) {
                intervalEmpGroups.push({
                    name: "By "+ groupType.GroupTypeName,
                    click: () => openReport(groupType.GroupLevel, ModuleReportType.Interval, groupType.BaseGroupType),
                });
                summaryEmpGroups.push({
                    name: "By "+ groupType.GroupTypeName,
                    click: () => openReport(groupType.GroupLevel, ModuleReportType.Summary, groupType.BaseGroupType),
                });
            } else {
                intervalContactGroups.push({
                    name: "By "+ groupType.GroupTypeName,
                    click: () => openReport(groupType.GroupLevel, ModuleReportType.Interval, groupType.BaseGroupType),
                });
                summaryContactGroups.push({
                    name: "By "+ groupType.GroupTypeName,
                    click: () => openReport(groupType.GroupLevel, ModuleReportType.Summary, groupType.BaseGroupType),
                });
            }
        });

        let intervalSubItems = [];
        let summarySubItems = [];

        intervalSubItems = intervalSubItems
            .concat(employeeGroupsHeader)
            .concat(intervalEmpGroups)
            .concat([{ type: ModuleManagerMenuItemType.Divider }])
            .concat(contactGroupsHeader)
            .concat(intervalContactGroups);

        summarySubItems = summarySubItems
            .concat(employeeGroupsHeader)
            .concat(summaryEmpGroups)
            .concat([{ type: ModuleManagerMenuItemType.Divider }])
            .concat(contactGroupsHeader)
            .concat(summaryContactGroups);

        const intervalReportMenuItem: IModuleManagerMenuItem = {
            name: this.getLocaleText("Open_Interval_Rep", "Open Interval Report"),
            icon: "report",
            type: ModuleManagerMenuItemType.SubMenu,
            subItems: [
                ...intervalSubItems,
            ],
            readOnlyEnabled: true,
        };

        const summaryReportMenuItem: IModuleManagerMenuItem = {
            ...intervalReportMenuItem,
            name: this.getLocaleText("Open_Summary_Rep", "Open Summary Report"),
            subItems: [
                ...summarySubItems,
            ],
        };
        this.moduleManager.addMenuItem(intervalReportMenuItem);
        this.moduleManager.addMenuItem(summaryReportMenuItem);
        //feature will be implemented in the future
        /*this.moduleManager.addMenuItem({
        //    name: "Gauge Range",
        //    icon: "gaugeRange",
        //    type: ModuleManagerMenuItemType.MenuItem,
        //    click: () => this.openRangeModal(),
        //})
        */
    }

}
